import { useEffect, useState, useMemo, memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkEmoji from "remark-emoji";
import { defaultSchema } from "hast-util-sanitize";
import { FaFile, FaFileAudio, FaFileCsv, FaFileExcel, FaFilePdf, FaFilePowerpoint, FaFileWord } from "react-icons/fa6";

const schema = {
    ...defaultSchema,
    tagNames: [...defaultSchema.tagNames, "sub", "sup", "table", "thead", "tbody", "tr", "th", "td", "blockquote", "code", "pre", "span"],
    attributes: {
        ...defaultSchema.attributes,
        sub: [],
        sup: [],
        span: [...(defaultSchema.attributes.span || [])],
        // span: [...(defaultSchema.attributes.span || []), ["className", /^hljs./]],
        code: [...(defaultSchema.attributes.code || [])],
        // code: [...(defaultSchema.attributes.code || []), ["className", /^hljs./]],
        pre: [...(defaultSchema.attributes.pre || [])],
        th: [...(defaultSchema.attributes.th || [])],
        td: [...(defaultSchema.attributes.td || [])],
    },
};

/**
 * Normaliza resposta para renderização fiél ao retorno
 */
export const formatText = (text, preLine) => {
    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkEmoji]}
            rehypePlugins={[rehypeRaw, rehypeHighlight, [rehypeSanitize, schema]]}
            components={{
                a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                p: ({ node, ...props }) => <p {...props} style={preLine ? { whiteSpace: "pre-line" } : undefined} />,
            }}
            className="message"
        >
            {text}
        </ReactMarkdown>
    );
};

/**
 * Força scroll com animação leve para o final do elemento
 */
export const handleScrollToBottom = (refElement, smooth = true) => {
    if (refElement.current) {
        refElement.current.scrollTo({ top: refElement.current.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    }
};

export const useAutoScroll = (refElement, deps = []) => {
    const tolerance = 60;
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [userScrolling, setUserScrolling] = useState(false);

    // Monitora scroll manual do usuário
    useEffect(() => {
        const el = refElement.current;
        if (!el) return;

        let scrollTimeout;

        const onScroll = () => {
            const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            setIsAtBottom(distanceFromBottom <= tolerance);

            // Marca que o usuário está mexendo no scroll
            setUserScrolling(true);
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => setUserScrolling(false), 200);
        };

        el.addEventListener("scroll", onScroll);
        return () => {
            el.removeEventListener("scroll", onScroll);
            clearTimeout(scrollTimeout);
        };
    }, [refElement, tolerance]);

    // Auto-scroll quando chegam novas mensagens
    useEffect(() => {
        const el = refElement.current;
        if (!el) return;

        // Só faz auto-scroll se:
        // 1. usuário está no fim
        // 2. usuário não está mexendo no scroll manualmente
        if (isAtBottom && !userScrolling) {
            el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
        }
    }, [...deps, isAtBottom, userScrolling]);
};

/**
 * Hook que anima a digitação da última mensagem da IA
 */
export const useTypingAnimation = (messages, delay = 5) => {
    const [typingMessage, setTypingMessage] = useState("");
    const [isTypingDone, setIsTypingDone] = useState(true);

    const lastAssistantMessage = useMemo(() => {
        const assistantMessages = messages.filter((m) => (m.chm_tipo_autor ?? m.ecm_tipo_autor) === "assistant");
        return assistantMessages.length ? assistantMessages[assistantMessages.length - 1] : null;
    }, [messages]);

    useEffect(() => {
        if (!lastAssistantMessage) return;

        let index = 0;
        const text = lastAssistantMessage.chm_mensagem ?? lastAssistantMessage.ecm_mensagem;

        if (lastAssistantMessage.isResponse) {
            setIsTypingDone(false);
            setTypingMessage("");

            const interval = setInterval(() => {
                setTypingMessage((prev) => {
                    const next = prev + text[index];
                    index++;
                    if (index >= text.length) {
                        clearInterval(interval);
                        setIsTypingDone(true);
                    }
                    return next;
                });
            }, delay);

            return () => clearInterval(interval);
        } else {
            setIsTypingDone(true);
            setTypingMessage(text);
        }
    }, [lastAssistantMessage]);

    return { typingMessage, isTypingDone, lastAssistantMessage };
};

export const FileIcon = memo(({ fileType, color }) => {
    if (fileType.includes("pdf")) {
        return <FaFilePdf size={16} color={`#${color}`} />;
    } else if (fileType.includes("audio")) {
        return <FaFileAudio size={16} color={`#${color}`} />;
    } else if (fileType.includes("word")) {
        return <FaFileWord size={16} color={`#${color}`} />;
    } else if (fileType.includes("powerpoint") || fileType.includes("officedocument.presentationml")) {
        return <FaFilePowerpoint size={16} color={`#${color}`} />;
    } else if (fileType.includes("excel") || fileType.includes("officedocument.spreadsheetml")) {
        return <FaFileExcel size={16} color={`#${color}`} />;
    } else if (fileType.includes("csv")) {
        return <FaFileCsv size={16} color={`#${color}`} />;
    } else {
        return <FaFile size={16} color={`#${color}`} />;
    }
});
